import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import {
  convertBorrowerApplication,
  getBorrowerApplication,
  listBorrowerApplications,
  rejectBorrowerApplication,
} from "../api/endpoints/borrower_applications"
import { Button } from "../components/ui/button"
import { DataTable, TableBody, TableHead, TableRow, Td, Th } from "../components/ui/data_table"
import { Drawer } from "../components/ui/drawer"
import { Input, Textarea } from "../components/ui/input"
import { StatusPill } from "../components/ui/status_pill"
import { useAuth } from "../stores/auth"

function statusTone(status: string) {
  if (status === "CONVERTED") return "success"
  if (status === "REJECTED" || status === "WITHDRAWN") return "danger"
  return "warning"
}

export function BorrowerApplicationsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState("")

  const listQuery = useQuery({
    queryKey: ["borrower-applications", page, search, status],
    queryFn: () =>
      listBorrowerApplications({
        page: String(page),
        per_page: "20",
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      }),
  })

  const detailQuery = useQuery({
    queryKey: ["borrower-application-detail", selectedApplicationId],
    queryFn: () => getBorrowerApplication(selectedApplicationId!),
    enabled: !!selectedApplicationId,
  })

  const rejectMutation = useMutation({
    mutationFn: () => rejectBorrowerApplication(selectedApplicationId!, rejectionReason),
    onSuccess: async () => {
      setRejectionReason("")
      await queryClient.invalidateQueries({ queryKey: ["borrower-applications"] })
      await queryClient.invalidateQueries({ queryKey: ["borrower-application-detail"] })
    },
  })

  const convertMutation = useMutation({
    mutationFn: () => convertBorrowerApplication(selectedApplicationId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["borrower-applications"] })
      await queryClient.invalidateQueries({ queryKey: ["borrower-application-detail"] })
    },
  })

  const rows = listQuery.data?.data ?? []
  const meta = listQuery.data?.meta
  const detail = detailQuery.data?.data
  const canAct = user?.role === "SUPER_ADMIN"

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Borrower Applications</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Review borrower-submitted applications before loan conversion.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search borrower, phone, product..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            className="w-64"
          />
          <select
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setStatus(event.target.value)
              setPage(1)
            }}
            value={status}
          >
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="REJECTED">Rejected</option>
            <option value="CONVERTED">Converted</option>
            <option value="WITHDRAWN">Withdrawn</option>
          </select>
        </div>
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-slate-200">
        <DataTable>
          <TableHead>
            <Th>Application</Th>
            <Th>Borrower</Th>
            <Th>Product</Th>
            <Th>Requested Amount</Th>
            <Th>KYC</Th>
            <Th>Status</Th>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => (
              <TableRow key={row.applicationId} onClick={() => setSelectedApplicationId(row.applicationId)}>
                <Td className="font-mono text-slate-600">{row.applicationId}</Td>
                <Td>
                  <div className="grid gap-1">
                    <span className="font-medium text-slate-800">{row.client.fullName}</span>
                    <span className="text-xs text-slate-500">{row.client.phoneNumber}</span>
                  </div>
                </Td>
                <Td>{row.product.name}</Td>
                <Td>{row.requestedPrincipalAmount}</Td>
                <Td>{row.client.kycStatus}</Td>
                <Td>
                  <StatusPill tone={statusTone(row.status) as any}>{row.status}</StatusPill>
                </Td>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
        {meta ? (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
            <span>
              Page {meta.page} of {meta.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button disabled={page <= 1} onClick={() => setPage((current) => current - 1)} size="sm" variant="secondary">
                Previous
              </Button>
              <Button
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((current) => current + 1)}
                size="sm"
                variant="secondary"
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <Drawer
        title="Borrower Application Detail"
        open={!!selectedApplicationId}
        onClose={() => {
          setSelectedApplicationId(null)
          setRejectionReason("")
        }}
      >
        {detail ? (
          <div className="space-y-6">
            <section className="space-y-2 text-sm">
              <p><strong>Application:</strong> {detail.applicationId}</p>
              <p><strong>Borrower:</strong> {detail.client.fullName}</p>
              <p><strong>Phone:</strong> {detail.client.phoneNumber}</p>
              <p><strong>Product:</strong> {detail.product.name}</p>
              <p><strong>Requested amount:</strong> {detail.requestedPrincipalAmount}</p>
              <p><strong>Payout method:</strong> {detail.paymentMethod}</p>
              <p><strong>Destination account:</strong> {detail.destinationAccount}</p>
              <p><strong>KYC status:</strong> {detail.client.kycStatus}</p>
              {detail.client.kycRejectionReason ? (
                <p><strong>KYC rejection reason:</strong> {detail.client.kycRejectionReason}</p>
              ) : null}
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">Documents</h3>
              <div className="space-y-2">
                {detail.documents?.map((document: any) => (
                  <div key={document.documentId} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <strong>{document.documentType}</strong>
                    <p className="text-slate-500">{document.fileName}</p>
                  </div>
                ))}
                {detail.documents?.length === 0 ? (
                  <p className="text-sm text-slate-500">No documents uploaded.</p>
                ) : null}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">Next of Kin</h3>
              <div className="space-y-2">
                {detail.nextOfKin?.map((nextOfKin: any) => (
                  <div key={nextOfKin.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <strong>{nextOfKin.fullName}</strong>
                    <p className="text-slate-500">
                      {nextOfKin.relationship} · {nextOfKin.phoneNumber}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {canAct ? (
              <section className="space-y-3">
                <Textarea
                  placeholder="Reason for rejection"
                  rows={3}
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                />
                <div className="flex items-center gap-3">
                  <Button
                    disabled={!rejectionReason.trim() || rejectMutation.isPending}
                    onClick={() => rejectMutation.mutate()}
                    variant="secondary"
                  >
                    {rejectMutation.isPending ? "Rejecting..." : "Reject"}
                  </Button>
                  <Button
                    disabled={detail.status !== "SUBMITTED" || convertMutation.isPending}
                    onClick={() => convertMutation.mutate()}
                  >
                    {convertMutation.isPending ? "Converting..." : "Convert to loan"}
                  </Button>
                </div>
              </section>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading application...</p>
        )}
      </Drawer>
    </div>
  )
}
